import * as React from 'react';
import { FC, useMemo } from 'react';

import {
  getBagSurchargeCents,
  getCartTotalCentsWithServerOrderSurcharges,
} from 'features/cart/utils/server-order-surcharges';
import { View } from 'react-native';

import Currency from 'components/currency';
import {
  getFeesByGroup,
  getStandardizedName,
} from 'experiments/checkout-v2/control/pages/cart/your-cart/totals/utils';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants';
import { computeOtherDiscountAmount } from 'utils/cart/helper';
import { getDeliveryFees } from 'utils/order';
import { geographicalFeeLabelIdMap, isMappedGeographicalFeeState } from 'utils/order';

import getRewardsDiscount from './get-rewards-discount';
import {
  FeeDeliveryItem,
  ReceiptDetail,
  ReceiptDetailsText,
  RewardsDiscountDetail,
  SDiscount,
} from './receipt-details.styled';
import { IOrderFooterProps } from './types';

const ReceiptDetails: FC<React.PropsWithChildren<IOrderFooterProps>> = ({ order }) => {
  const { formatCurrencyForLocale: currencyFormatter } = useUIContext();
  const { delivery } = order;
  const { subTotalCents, taxCents } = order.cart;

  const {
    feeCents: deliveryFeeCents,
    isFreeDelivery,
    deliveryFeeDiscountCents,
    geographicalFeeCents,
    serviceFeeCents,
    smallCartFeeCents,
  } = getDeliveryFees({
    delivery,
  });

  const enableFeesOnOrderReceipt = useFlag(LaunchDarklyFlag.ENABLE_FEES_ON_ORDER_RECEIPT);
  const enableDiscountsOnOrderReceipt = useFlag(LaunchDarklyFlag.ENABLE_DISCOUNTS_ON_ORDER_RECEIPT);
  const enablePosOrderSurchargesAsFees = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_POS_ORDER_SURCHARGES_AS_FEES)
  );

  const discounts = useMemo(() => order.cart.discounts || [], [order.cart.discounts]);
  const dropoffState = delivery?.dropoff?.state;

  // Use a custom label for geographical delivery fees as required
  const geographicalFeeLabelId = (isMappedGeographicalFeeState(dropoffState)
    ? geographicalFeeLabelIdMap[dropoffState]
    : 'geographicalFee') as TranslationKey;

  const rewardsDiscountCents = useMemo(
    () => getRewardsDiscount({ discounts, enableDiscountsOnOrderReceipt }),
    [discounts, enableDiscountsOnOrderReceipt]
  );

  const otherDiscountAmount = computeOtherDiscountAmount(discounts);

  const fees = order.cart.fees ?? [];
  const itemFees = getFeesByGroup(fees, enablePosOrderSurchargesAsFees);
  const bagSurchargeCents = getBagSurchargeCents(fees);
  const adjustedTotalCents = getCartTotalCentsWithServerOrderSurcharges({
    cart: order.cart,
    enablePosOrderSurchargesAsFees,
  });

  return (
    <View>
      {enableFeesOnOrderReceipt &&
        !!itemFees &&
        Object.entries(itemFees).map(([name, entryCents]: [string, number], index) => {
          const standardizedName = getStandardizedName({ name });
          const feeTranslationKey = `${standardizedName}Fee` as TranslationKey;
          return (
            <ReceiptDetail key={index}>
              <ReceiptDetailsText>{t(feeTranslationKey)}</ReceiptDetailsText>

              <ReceiptDetailsText>{currencyFormatter(entryCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          );
        })}
      {enablePosOrderSurchargesAsFees && bagSurchargeCents > 0 && (
        <ReceiptDetail>
          <ReceiptDetailsText>{t('bagFee', 'Bag Fee')}</ReceiptDetailsText>
          <ReceiptDetailsText>{currencyFormatter(bagSurchargeCents)}</ReceiptDetailsText>
        </ReceiptDetail>
      )}
      {order.delivery && (
        <View>
          <ReceiptDetail>
            <ReceiptDetailsText>{t('deliveryFee')}:</ReceiptDetailsText>
            {isFreeDelivery ? (
              <FeeDeliveryItem>
                {t('free')}
                &nbsp;
                <SDiscount>
                  <Currency amount={deliveryFeeDiscountCents} />
                </SDiscount>
              </FeeDeliveryItem>
            ) : (
              <ReceiptDetailsText>{currencyFormatter(deliveryFeeCents)}</ReceiptDetailsText>
            )}
          </ReceiptDetail>
          {geographicalFeeCents > 0 && (
            <ReceiptDetail>
              <ReceiptDetailsText>{t(geographicalFeeLabelId)}:</ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(geographicalFeeCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          )}
          {serviceFeeCents > 0 && (
            <ReceiptDetail>
              <ReceiptDetailsText>{t('serviceFee')}:</ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(serviceFeeCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          )}
          {smallCartFeeCents > 0 && (
            <ReceiptDetail>
              <ReceiptDetailsText>{t('smallCartFee')}:</ReceiptDetailsText>
              <ReceiptDetailsText>{currencyFormatter(smallCartFeeCents)}</ReceiptDetailsText>
            </ReceiptDetail>
          )}
          <ReceiptDetail>
            <ReceiptDetailsText>{t('tip')}:</ReceiptDetailsText>
            <ReceiptDetailsText>{currencyFormatter(order.delivery.tipCents)}</ReceiptDetailsText>
          </ReceiptDetail>
        </View>
      )}
      {enableDiscountsOnOrderReceipt && !!otherDiscountAmount && (
        <RewardsDiscountDetail>
          <ReceiptDetailsText>{t('otherDiscounts')}</ReceiptDetailsText>
          <ReceiptDetailsText>{` - ${currencyFormatter(otherDiscountAmount)}`}</ReceiptDetailsText>
        </RewardsDiscountDetail>
      )}
      {!!rewardsDiscountCents && (
        <RewardsDiscountDetail>
          <ReceiptDetailsText color={primitive.$crunchyGreen}>
            {t('rewardSavings')}
          </ReceiptDetailsText>
          <ReceiptDetailsText color={primitive.$crunchyGreen}>
            {` - ${currencyFormatter(rewardsDiscountCents)}`}
          </ReceiptDetailsText>
        </RewardsDiscountDetail>
      )}

      <ReceiptDetail>
        <ReceiptDetailsText>{t('subtotal')}</ReceiptDetailsText>
        <ReceiptDetailsText>{currencyFormatter(subTotalCents)}</ReceiptDetailsText>
      </ReceiptDetail>
      <ReceiptDetail>
        <ReceiptDetailsText>{t('tax')}</ReceiptDetailsText>
        <ReceiptDetailsText>{currencyFormatter(taxCents)}</ReceiptDetailsText>
      </ReceiptDetail>
      <ReceiptDetail $total>
        <ReceiptDetailsText $total>{t('total')}</ReceiptDetailsText>
        <ReceiptDetailsText $total>{currencyFormatter(adjustedTotalCents)}</ReceiptDetailsText>
      </ReceiptDetail>
    </View>
  );
};

export default ReceiptDetails;
